// src/layouts/DashboardLayout.tsx
//
// 대시보드 크롬 — 홈/로그인과 동일한 클린 디자인 시스템.
// 흰 사이드바 + 얇은 보더, 단일 액센트(#E2451C), 라운드 8~12px.
// (전역 theme.ts 의 매니페스트 스타일은 이 화면에서 sx 로 무력화한다)

import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AppBar,
  Avatar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import DescriptionIcon from '@mui/icons-material/Description';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import FactoryIcon from '@mui/icons-material/Factory';
import AddBoxIcon from '@mui/icons-material/AddBox';
import InventoryIcon from '@mui/icons-material/Inventory';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import MonitorIcon from '@mui/icons-material/Monitor';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import ArticleIcon from '@mui/icons-material/Article';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import ForumIcon from '@mui/icons-material/Forum';
import { designTokens } from '../theme.ts';

const DRAWER_WIDTH = 268;

interface NavItem {
  label: string;
  code: string;            // 데이터로만 유지 — 화면에는 렌더하지 않는다
  path: string;
  icon: React.ReactNode;
}

interface NavGroup {
  title: string;
  code: string;            // 데이터로만 유지 — 화면에는 렌더하지 않는다
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    title: '', code: 'OVR',
    items: [
      { label: '홈', code: '00', path: '/dashboard', icon: <HomeIcon /> },
    ],
  },
  {
    title: '견적 관리', code: 'EST',
    items: [
      { label: '견적서 작성',         code: '01', path: '/dashboard/estimate/create',            icon: <DescriptionIcon /> },
      { label: '견적서 작성 (문)',    code: '02', path: '/dashboard/estimate/mun/create',        icon: <ArticleIcon /> },
      { label: '비교견적서',          code: '03', path: '/dashboard/estimate/comparison/create', icon: <CompareArrowsIcon /> },
      { label: '견적서 조회/수정',    code: '04', path: '/dashboard/estimate/read',              icon: <ListAltIcon /> },
    ],
  },
  {
    title: '생산 관리', code: 'MFG',
    items: [
      { label: '생산요청서 (렌트)',  code: '01', path: '/dashboard/product-req-rents',      icon: <FactoryIcon /> },
      { label: '생산요청서 등록',    code: '02', path: '/dashboard/product-req-rents-form', icon: <AddBoxIcon /> },
      { label: '제품 모니터',        code: '03', path: '/dashboard/product/monitor',        icon: <InventoryIcon /> },
    ],
  },
  {
    title: '반품 관리', code: 'RTN',
    items: [
      { label: '반품 요청서', code: '01', path: '/dashboard/return-request/form',    icon: <AssignmentReturnIcon /> },
      { label: '반품 모니터', code: '02', path: '/dashboard/return-request/monitor', icon: <MonitorIcon /> },
    ],
  },
  {
    title: '고객 서비스', code: 'CS·',
    items: [
      { label: '상담 기록', code: '01', path: '/dashboard/customerservice/calllog', icon: <SupportAgentIcon /> },
    ],
  },
  {
    title: '세금계산서 관리', code: 'TAX',
    items: [
      { label: '세금계산서 발행 등록', code: '01', path: '/dashboard/container-rental/create', icon: <AddBusinessIcon /> },
      { label: '세금계산서 발행 목록', code: '02', path: '/dashboard/container-rental',         icon: <LocalShippingIcon /> },
      { label: '임대 타임라인',        code: '03', path: '/dashboard/container-rental/timeline', icon: <ViewTimelineIcon /> },
    ],
  },
  // 피드백은 영업 업무 도메인 데이터가 아니라 도구 자체에 대한 메타 기능이라
  // 기존 5개 도메인 그룹 어디에도 속하지 않는다. 특히 「고객 서비스」에 넣으면
  // 안 된다 — 그 그룹의 "상담 기록"은 고객 대상 기록이고 피드백은 사내
  // 의견이라, 같은 그룹이면 "고객 불만을 여기 적나?" 라는 오해가 생긴다.
  // 맨 아래에 두는 이유는 접근 빈도가 업무 메뉴보다 낮기 때문이다.
  // 항목은 하나뿐이다 — 글쓰기 진입점은 목록 헤더의 [글쓰기] 버튼 하나로 충분하다.
  {
    title: '의견 · 개선', code: 'FBK',
    items: [
      { label: '피드백 게시판', code: '01', path: '/dashboard/feedback', icon: <ForumIcon /> },
    ],
  },
];

// 홈/로그인과 동일한 팔레트
const C = {
  canvas: '#F6F7F8',
  surface: '#FFFFFF',
  border: '#E4E7EA',
  ink: '#171A1E',
  sub: '#6B7178',
  caption: '#9AA0A6',
  accent: '#E2451C',
  accentHover: '#C93A14',
  accentTint: '#FDEDE8',
  neutral: '#F2F4F5',
  shadow: '0 8px 24px rgba(16,24,32,0.08)',
};

const SANS = designTokens.fonts.sans;
const DISPLAY = designTokens.fonts.display;

// 텍스트 버튼용 기본 리셋
const TEXT_BUTTON_RESET = {
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  fontFamily: SANS,
  textAlign: 'left' as const,
  transition: 'color 150ms ease',
};

const MENU_SLOT_PROPS = {
  paper: {
    sx: {
      mt: 0.75,
      minWidth: 168,
      borderRadius: '10px',
      border: `1px solid ${C.border}`,
      boxShadow: C.shadow,
      '& .MuiMenuItem-root': {
        fontFamily: SANS,
        fontSize: 14,
        color: C.ink,
        borderRadius: '6px',
        mx: 0.5,
        my: 0.25,
        minHeight: 38,
        '&:hover': { bgcolor: C.neutral },
      },
      '& .MuiListItemIcon-root': { minWidth: 30, color: C.sub },
    },
  },
};

export default function DashboardLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, loginWithRedirect, logout } = useAuth0();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleNavClick = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };
  const handleUserMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);
  const handleLogout = () => { handleCloseMenu(); logout({ logoutParams: { returnTo: window.location.origin } }); };
  const handleLogin = async () => {
    try { await loginWithRedirect(); }
    catch (e: any) {
      if (e.error === 'access_denied') alert('관리자 승인 대기 중입니다.');
    }
  };

  const displayName = (user && (user.name || user.nickname || user.email)) || '';
  const initial = displayName.trim().charAt(0).toUpperCase() || 'U';

  // 상단 브레드크럼용 — navGroups 를 읽기만 한다(active 판정 로직과는 무관).
  // 완전일치 우선, 없으면 최장 접두사 매칭 — 상세(/container-rental/:id)나
  // 수정(/edit/:id)처럼 메뉴에 없는 하위 경로도 소속 메뉴의 크럼을 얻는다.
  const flatNav = navGroups
    .flatMap((g) => g.items.map((i) => ({ groupTitle: g.title, label: i.label, path: i.path })));
  const currentEntry =
    flatNav.find((i) => i.path === location.pathname) ??
    flatNav
      .filter((i) => location.pathname.startsWith(i.path + '/'))
      .sort((a, b) => b.path.length - a.path.length)[0];
  const crumbGroup = currentEntry?.groupTitle || '';
  const crumbLabel = currentEntry?.label || '대시보드';

  const wordmark = (size: number, color: string) => (
    <Typography
      component="div"
      sx={{
        fontFamily: DISPLAY,
        fontWeight: 800,
        fontSize: size,
        lineHeight: 1,
        letterSpacing: '-0.01em',
        color,
      }}
    >
      MARS
      <Box component="span" sx={{ color: C.accent }}>.</Box>
    </Typography>
  );

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: C.surface }}>
      {/* 브랜드 */}
      <Box sx={{ px: '24px', py: '20px', borderBottom: `1px solid ${C.border}` }}>
        {wordmark(20, C.ink)}
        <Typography sx={{ mt: '6px', fontFamily: SANS, fontSize: 13, lineHeight: 1.4, color: C.sub }}>
          영업관리
        </Typography>
      </Box>

      {/* 내비게이션 */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: '12px' }}>
        {navGroups.map((group, gi) => (
          <List key={gi} disablePadding>
            {group.title ? (
              <Box
                component="div"
                sx={{
                  px: '24px',
                  pt: gi > 0 ? '18px' : '4px',
                  pb: '8px',
                  fontFamily: SANS,
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: 1.4,
                  letterSpacing: '0.08em',
                  color: C.caption,
                }}
              >
                {group.title}
              </Box>
            ) : null}

            {group.items.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <ListItem key={item.path} disablePadding sx={{ px: '12px', mb: '2px' }}>
                  <ListItemButton
                    selected={isActive}
                    onClick={() => handleNavClick(item.path)}
                    sx={{
                      minHeight: 40,
                      px: '12px',
                      py: 0,
                      gap: '10px',
                      borderRadius: '8px',
                      transition: 'background-color 150ms ease, color 150ms ease',
                      '&:hover': { bgcolor: C.neutral },
                      '&.Mui-selected': {
                        bgcolor: C.accentTint,
                        '&:hover': { bgcolor: C.accentTint },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                        color: isActive ? C.accentHover : C.sub,
                        '& svg': { fontSize: 20 },
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      noWrap
                      sx={{
                        fontFamily: SANS,
                        fontSize: 14,
                        lineHeight: 1.4,
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? C.accentHover : C.ink,
                      }}
                    >
                      {item.label}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        ))}
      </Box>

      {/* 사용자 영역 */}
      <Box sx={{ p: '12px', borderTop: `1px solid ${C.border}` }}>
        {isAuthenticated && user ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '12px', py: '10px' }}>
            <Avatar
              alt={displayName}
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: '50%',
                bgcolor: C.ink,
                color: '#FFFFFF',
                fontFamily: SANS,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {initial}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Box
                component="button"
                type="button"
                onClick={() => handleNavClick('/dashboard/profile')}
                sx={{
                  ...TEXT_BUTTON_RESET,
                  maxWidth: '100%',
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: 1.4,
                  color: C.ink,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  '&:hover': { color: C.accentHover },
                }}
              >
                {displayName}
              </Box>
              <Box
                component="button"
                type="button"
                onClick={handleLogout}
                sx={{
                  ...TEXT_BUTTON_RESET,
                  mt: '2px',
                  fontSize: 12,
                  lineHeight: 1.4,
                  color: C.sub,
                  '&:hover': { color: C.accentHover },
                }}
              >
                로그아웃
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            component="button"
            type="button"
            onClick={handleLogin}
            sx={{
              ...TEXT_BUTTON_RESET,
              width: '100%',
              height: 40,
              borderRadius: '8px',
              bgcolor: C.accent,
              color: '#FFFFFF',
              fontSize: 14,
              fontWeight: 700,
              textAlign: 'center',
              transition: 'background-color 160ms ease',
              '&:hover': { bgcolor: C.accentHover },
            }}
          >
            로그인
          </Box>
        )}
      </Box>
    </Box>
  );

  const userMenu = (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleCloseMenu}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      slotProps={MENU_SLOT_PROPS}
    >
      <MenuItem onClick={() => { handleCloseMenu(); navigate('/dashboard/profile'); }}>
        <ListItemIcon><PersonIcon sx={{ fontSize: 18 }} /></ListItemIcon>
        프로필
      </MenuItem>
      <MenuItem onClick={handleLogout}>
        <ListItemIcon><LogoutIcon sx={{ fontSize: 18 }} /></ListItemIcon>
        로그아웃
      </MenuItem>
    </Menu>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: C.canvas }}>
      <CssBaseline />

      {/* 모바일 앱바 */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          display: { md: 'none' },
          bgcolor: C.surface,
          color: C.ink,
          borderBottom: `1px solid ${C.border}`,
          boxShadow: 'none',
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ color: C.ink, borderRadius: '8px', '&:hover': { bgcolor: C.neutral } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ ml: 1.5 }}>{wordmark(18, C.ink)}</Box>
          <Box sx={{ flex: 1 }} />
          {isAuthenticated && user && (
            <>
              <IconButton
                onClick={handleUserMenu}
                sx={{ borderRadius: '50%', p: 0.5, '&:hover': { bgcolor: C.neutral } }}
              >
                <Avatar
                  alt={displayName}
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    bgcolor: C.ink,
                    color: '#FFFFFF',
                    fontFamily: SANS,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {initial}
                </Avatar>
              </IconButton>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* 사이드바 */}
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              bgcolor: C.surface,
              color: C.ink,
              borderRight: `1px solid ${C.border}`,
              backgroundImage: 'none',
            },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              bgcolor: C.surface,
              color: C.ink,
              borderRight: `1px solid ${C.border}`,
              backgroundImage: 'none',
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* 메인 */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minWidth: 0,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: C.canvas,
        }}
      >
        <Toolbar sx={{ display: { md: 'none' } }} />

        {/* 데스크톱 상단 바 */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            minHeight: 56,
            px: 3,
            gap: 2,
            bgcolor: C.surface,
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          {/* 브레드크럼 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: 0,
              fontFamily: SANS,
              fontSize: 13,
              lineHeight: 1.4,
            }}
          >
            <Box component="span" sx={{ color: C.sub }}>MARS</Box>
            {crumbGroup && (
              <>
                <Box component="span" sx={{ color: C.caption }}>/</Box>
                <Box component="span" sx={{ color: C.sub }}>{crumbGroup}</Box>
              </>
            )}
            <Box component="span" sx={{ color: C.caption }}>/</Box>
            <Box
              component="span"
              sx={{ color: C.ink, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {crumbLabel}
            </Box>
          </Box>

          {isAuthenticated && user ? (
            <>
              <Box
                component="button"
                type="button"
                onClick={handleUserMenu}
                sx={{
                  ...TEXT_BUTTON_RESET,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  height: 36,
                  px: '10px',
                  borderRadius: '8px',
                  transition: 'background-color 150ms ease',
                  '&:hover': { bgcolor: C.neutral },
                }}
              >
                <Avatar
                  alt={displayName}
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    bgcolor: C.ink,
                    color: '#FFFFFF',
                    fontFamily: SANS,
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {initial}
                </Avatar>
                <Typography noWrap sx={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.ink, maxWidth: 180 }}>
                  {displayName}
                </Typography>
              </Box>
            </>
          ) : (
            <Box
              component="button"
              type="button"
              onClick={handleLogin}
              sx={{
                ...TEXT_BUTTON_RESET,
                height: 36,
                px: '18px',
                borderRadius: '8px',
                bgcolor: C.accent,
                color: '#FFFFFF',
                fontSize: 14,
                fontWeight: 700,
                transition: 'background-color 160ms ease',
                '&:hover': { bgcolor: C.accentHover },
              }}
            >
              로그인
            </Box>
          )}
        </Box>

        {/* 페이지 콘텐츠 */}
        <Box sx={{ flex: 1, minWidth: 0, p: { xs: 2, md: 3 }, overflow: 'auto', bgcolor: C.canvas }}>
          <Outlet />
        </Box>
      </Box>

      {isAuthenticated && user && userMenu}
    </Box>
  );
}
